<template>
  <div
    class="product-form-option"
    :class="{
      'gift-card': isGiftCard
    }"
  >
    <div class="product-form-option__header">
      <div
        v-if="name"
        class="product-form-option__name text-body-s"
        v-html="formattedName"
      />

      <component
        :is="linkComponent"
        v-if="hasLink && !modal.handler"
        :href="link.url"
        class="product-form-option__link text-body-xs-link"
        @click="link.handler"
        v-text="link.label"
      />
      <component
        :is="linkComponent"
        v-if="modal.handler"
        class="product-form-option__link text-body-xs-link"
        @click="modal.handler"
        v-text="link.label"
      />
    </div>

    <div
      class="product-form-option__values"
      :class="{
        'gift-card': isGiftCard
      }"
    >
      <div
        v-for="(value) of values"
        :key="value"
        class="product-form-option__value"
      >
        <input
          v-if="!value.url"
          :id="getValueKey(value)"
          v-model="selected"
          :name="name"
          type="radio"
          :value="value.label"
          :disabled="!allowSelection && value.disabled"
          class="product-form-option__radio visually-hidden"
        >

        <component
          :is="getValueLabelComponent(value)"
          :for="getValueKey(value)"
          class="product-form-option__label text-body-s"
          :class="{
            'product-form-option__label--image': value.image || value.asImage,
            'product-form-option__label--selected': value.selected,
            'product-form-option__label--disabled': value.disabled
          }"
          :href="value.url"
        >
          <responsive-image
            v-if="value.image || value.asImage"
            block-class="product-form-option"
            :image="value.image"
            min-max="200-400"
            ratio="56:70"
            :lazyload="false"
          />

          <span
            v-show="!value.image && !value.asImage"
            v-text="optionText(value)"
          />
        </component>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product form option (product-form-option)
 * -----------------------------------------------------------------------------
 * A product variant option with swatch support.
 *
 * @param {String} name - The option name.
 * @param {Object[]} values - The option values.
 * @param {String} values[].label - The value label.
 * @param {String} values[].title - The alternative title for the value.
 * @param {String} values[].url - Adds a link to the label.
 * @param {Object} values[].image - Optional swatch image.
 * @param {Boolean} values[].selected - Determines if the value is selected.
 * @param {Boolean} values[].disabled - Stops the value from being selected.
 * @param {Object} link - Optional option link.
 * @param {String} link.label - The link label.
 * @param {String} link.url - The link target.
 * @param {Function} link.handler - Handles the click event, overrides the
 * `url`.
 * @param {Boolean} allowSelection - Allows options to be selected.
 * @param {Object} modal - Modal data to open, empty if link is to be used
 *
 */
import ResponsiveImage from '~global/images/responsive-image'

import { convertToHandle } from '~/helpers/convert'
import { formatMoney } from '@shopify/theme-currency'

/**
 * Returns the default selected value.
 * @param {Array} values - The option values.
 * @returns {Boolean|String}
 */
function getDefaultSelected(values) {
  const selected = values && values.find((value) => value.selected)

  if (!selected) {
    return false
  }

  return selected.label
}

export default {
  name: 'ProductFormOption',

  components: {
    ResponsiveImage,
  },

  props: {
    allowSelection: {
      type: Boolean,
      default: false,
    },
    modal: {
      type: Object,
      default: () => ({}),
    },
    isGiftCard: {
      type: Boolean,
      default: false,
    },
    link: {
      type: [Boolean, Object],
      default: () => ({}),
    },
    name: {
      type: String,
      default: '',
    },
    values: {
      type: Array,
      default: () => [],
    },
  },

  emits: {
    change: (payload) => typeof payload === 'object',
  },

  data() {
    return {
      selected: getDefaultSelected(this.values),
    }
  },

  computed: {

    /**
     * Returns the formatted option name.
     * @returns {String}
     */
    formattedName() {
      if (
        this.selected &&
        this.selectedValueObject &&
        this.selectedValueObject.title
      ) {
        return `
          ${this.name}:
          <span class="text-body-s-bold">
            ${this.selectedValueObject.title}
          </span>
        `
      }

      return this.name
    },

    /**
     * Returns the selected value object.
     * @returns {Object}
     */
    selectedValueObject() {
      return this.values.find(({ label }) => label === this.selected)
    },

    /**
     * Returns the component for the link element.
     * @returns {String}
     */
    linkComponent() {
      if (this.link.handler) {
        return 'button'
      }

      if (this.link.url) {
        return 'a'
      }

      return 'span'
    },

    /**
     * Returns if the link element exists.
     * @returns {Boolean}
     */
    hasLink() {
      return this.link &&
        this.link.label &&
        (this.link.handler || this.link.url)
    },
  },

  watch: {

    /**
     * Emits the change event when the selected value changes.
     */
    selected() {
      this.$emit('change', { name: this.name, value: this.selected })
    },

    values: {

      /**
       * Updates the default selected option if the values change.
       */
      handler() {
        this.selected = getDefaultSelected(this.values)
      },

      deep: true,
    },
  },

  methods: {

    /**
     * Returns the key for the given value.
     * @param {Object} value - The value object.
     * @returns {String}
     */
    getValueKey(value) {
      if (value.url) {
        return null
      }

      return `option-${convertToHandle(`${this.name}-${value.label}`)}`
    },

    /**
     * Returns the element for the label.
     * @param {Object} value - The value object.
     * @returns {String}
     */
    getValueLabelComponent(value) {
      if (!value.url || this.selected === value.label) {
        return 'label'
      }

      return 'a'
    },

    /**
     * Get option text.
     * @returns {String}
     */
    optionText(option) {
      if (this.name.toLowerCase() === 'value') {
        const price = option.optionVariant?.price?.amount || option.label

        return `${price ? formatMoney(price, `${window.cnvs.store.currencySymbol}{{amount_no_decimals}}`) : option.label}`
      }

      return option.label

    },
  },
}
</script>

<style lang="scss">
@import './product-form-option';
</style>
